import React from 'react';
import DoneAllIcon from '@markinson/uicomponents-v2/SvgIcons/DoneAll';
import { Button, FormControlLabel, Switch, withStyles } from '@material-ui/core';
import styles from './PriceCalculator.styles';
import { IPriceCalculatorHandle, IPriceCalculatorProperties } from './PriceCalculator.properties';
import CalculatorSummaryPanel from './SummaryPanel/CalculatorSummaryPanel';
import TextField from '@markinson/uicomponents-v2/TextFieldV1';
import { useCPLContextSelector, useCPLDispatch } from '../CustomerPricingLookup.context';
import { CustomerPricingLookupActionTypes } from '../CustomerPricingLookup.actions';
import ExtendedDisplayField from 'components/FormView/Fields/ExtendedDisplayField/ExtendedDisplayField';

const PriceCalculator = (props: IPriceCalculatorProperties, ref: React.Ref<IPriceCalculatorHandle>): JSX.Element => {
  const { classes, onCalculate } = props;
  const [form, setFormValues] = React.useState<any>({});
  const defaultDecimalPlaces = 4;
  const initialValues = useCPLContextSelector<'InitialCalculatorData'>((state) => state.InitialCalculatorData);
  const contextDispatch = useCPLDispatch();
  const costFieldRef = React.useRef<any>(null);
  const [gpError, setGPError] = React.useState<boolean>(false);
  const [taxRateError, setTaxRateError] = React.useState<boolean>(false);

  React.useEffect(
    () => {
      if (initialValues) {
        setFormValues({ ...initialValues });
      }
    },
    [initialValues]
  );

  React.useEffect(
    () => {
      if (form) {
        contextDispatch({
          [CustomerPricingLookupActionTypes.setCalculatedData]: form,
        });
      }
    },
    [form]
  );

  const updateFormField = (name, value) => {
    if (form[name] !== value) {
      setFormValues((prevForm) => ({
        ...prevForm,
        [name]: value
      }));
    }
  };

  const isValid = () => {
    return !(taxRateError || gpError);
  };

  const fieldValidation = (value, minimumValue, maximumValue) => {
    return (value < minimumValue || value > maximumValue);
  };

  const onTaxRateChange = (t: any) => {
    if (Number(t.target.value) || t.target.value === '0' || t.target.value === '-0') {
      updateFormField('TaxRateDisplay', Number(t.target.value));
      updateFormField('TaxRate', Number(t.target.value));
      setTaxRateError(fieldValidation(Number(t.target.value), form?.TaxRateMinimumValue ?? 0, form?.TaxRateMaximumValue));
    } else {
      updateFormField('TaxRateDisplay', t.target.value);
      updateFormField('TaxRate', 0);
    }
  };

  const onGPChange = (t: any) => {
    if (Number(t.target.value) || t.target.value === '0' || t.target.value === '-0') {
      updateFormField('GrossProfitDisplay', Number(t.target.value));
      updateFormField('GrossProfit', Number(t.target.value));
      setGPError(fieldValidation(Number(t.target.value), form?.GrossProfitMinimumValue, form?.GrossProfitMaximumValue));
    } else {
      updateFormField('GrossProfitDisplay', t.target.value);
      updateFormField('GrossProfit', 0);
    }
  };

  React.useImperativeHandle(
    ref,
    () => ({
      focusFirstField(): void {
        costFieldRef?.current?.focus();
      }
    }),
    []
  );

  return (
    <div className={classes.resultContainer}>
      <div className={classes.leftContainer}>
        <div className={classes.calculatorDialogContainer}>
          <div className={classes.calculatorDialog}>
            <div className={classes.costContainer}>
              <TextField
                fieldRef={(costRef) => { costFieldRef.current = costRef; }}
                size={'medium'}
                label={'Cost'}
                type={'number'}
                name={'Cost'}
                decimalSeperater={true}
                decimalPlaces={defaultDecimalPlaces}
                value={form.CostDisplay}
                onChange={(t) => { updateFormField('CostDisplay', t.target.value); updateFormField('Cost', t.target.value); }}
                onBlur={(t) => { updateFormField('CostDisplay', Number(t.target.value || 0).toFixed(4)); }}
              />
              <FormControlLabel
                control={<Switch
                  color={'default'}
                  checked={form?.TaxIncludedCosts || false}
                  classes={{
                    switchBase: classes.switchBase,
                    checked: classes.checked,
                    bar: classes.bar,
                  }}
                />}
                style={{ height: '70px', paddingLeft: '10px' }}
                classes={{
                  label: classes.formControlLabel,
                  disabled: classes.disabled
                }}
                label='Tax included in cost'
                onChange={(_, value) => {
                  updateFormField('TaxIncludedCosts', value);
                }}
              />
            </div>
            <div>
              <ExtendedDisplayField
                {
                ...{
                  label: 'GP',
                  type: 'number',
                  adornment: '%',
                  size: 'small',
                  decimalPlaces: 2,
                  value: form?.GrossProfitDisplay,
                  onChange: onGPChange,
                  onBlur: (t) => {
                    updateFormField('GrossProfitDisplay', Number(t.target.value).toFixed(2));
                    setGPError(fieldValidation(Number(t.target.value).toFixed(2), form?.GrossProfitMinimumValue, form?.GrossProfitMaximumValue));
                  },
                  error: gpError,
                  helperText: gpError ? `Invalid GrossProfit value of ${form?.GrossProfit} Value must be between -999.99 and 999.99.` : '',
                }
                }

              />
            </div>
            <div>
              <div>
                <ExtendedDisplayField
                  {
                  ...{
                    label: 'Tax rate',
                    type: 'number',
                    adornment: '%',
                    size: 'small',
                    decimalPlaces: 2,
                    value: form?.TaxRateDisplay,
                    readOnly: (form?.TaxIncludedCosts === form?.TaxIncludedPrice),
                    onChange: onTaxRateChange,
                    onBlur: (t) => { updateFormField('TaxRateDisplay', Number(t.target.value).toFixed(2)); setTaxRateError(fieldValidation(Number(t.target.value).toFixed(2), form?.TaxRateMinimumValue ?? 0, form?.TaxRateMaximumValue)); },
                    error: taxRateError,
                    helperText: taxRateError ? `Invalid TaxRate value of ${form?.TaxRateDisplay} Value must be between 0 and 99.99.` : '',
                  }
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <div className={classes.calculatorFooter}>
          <Button
            variant={'contained'}
            className={classes.calculateButton}
            disabled={taxRateError || gpError}
            onClick={onCalculate}>
            <DoneAllIcon style={{ color: isValid() ? '#006633' : 'rgba(0, 0, 0, 0.26)' }} /> CALCULATE
          </Button>
        </div>
      </div>
      <div className={classes.rightContainer}>
        <CalculatorSummaryPanel data={{ title: form?.TaxIncludedPrice ? 'PRICE (INC. TAX)' : 'PRICE (EX.TAX)', PriceDisplay: form?.SellPrice }} />
      </div>
    </div>
  );
};

export default withStyles(styles, { index: 1 })(React.forwardRef(PriceCalculator));
